import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, forkJoin } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroCheck,
  heroEnvelope,
  heroNoSymbol,
  heroPlus,
  heroShieldCheck,
  heroTrash,
} from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import type { AuthPermission } from '../../../core/api/generated/models/AuthPermission';
import { AuthStore } from '../../../core/auth/auth.store';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableActions,
  UiDataTableCell,
  UiDataTableEmptyCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiEmptyState,
  UiFormField,
  UiHint,
  UiInput,
  UiLabel,
  UiSectionHeader,
  UiSelect,
  UiSelectDropdown,
  UiSelectField,
  UiSelectOption,
  UiPageHeader,
  type UiSelectFieldOption,
} from '../../../shared/ui';
import {
  SettingsCoreApiService,
  type CoreEmailDomain,
  type CoreInvitation,
  type CoreMembership,
  type CoreRole,
  type CoreRoleBinding,
  type CoreTeam,
  type CoreTeamMember,
} from '../settings-core-api.service';

@Component({
  selector: 'app-users-settings-page',
  imports: [
    DatePipe,
    NgIcon,
    NgpSelectPortal,
    ReactiveFormsModule,
    UiBadge,
    UiButton,
    UiDataTable,
    UiDataTableActions,
    UiDataTableCell,
    UiDataTableEmptyCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiEmptyState,
    UiFormField,
    UiHint,
    UiInput,
    UiLabel,
    UiPageHeader,
    UiSectionHeader,
    UiSelect,
    UiSelectDropdown,
    UiSelectField,
    UiSelectOption,
  ],
  providers: [
    provideIcons({
      heroArrowPath,
      heroCheck,
      heroEnvelope,
      heroNoSymbol,
      heroPlus,
      heroShieldCheck,
      heroTrash,
    }),
  ],
  templateUrl: './users-settings.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersSettingsPage {
  private readonly api = inject(SettingsCoreApiService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly toastr = inject(ToastrService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly memberships = signal<CoreMembership[]>([]);
  readonly roles = signal<CoreRole[]>([]);
  readonly roleBindings = signal<CoreRoleBinding[]>([]);
  readonly teams = signal<CoreTeam[]>([]);
  readonly teamMembers = signal<CoreTeamMember[]>([]);
  readonly invitations = signal<CoreInvitation[]>([]);
  readonly domains = signal<CoreEmailDomain[]>([]);
  readonly membershipSavingIds = signal<string[]>([]);
  readonly invitationSavingIds = signal<string[]>([]);
  readonly domainSavingIds = signal<string[]>([]);
  readonly businessId = computed(() => this.authStore.business()?.id ?? null);
  readonly canInviteMembers = computed(() => this.hasPermission('core:memberships:invite'));
  readonly canUpdateMemberships = computed(() => this.hasPermission('core:memberships:update'));
  readonly canRemoveMemberships = computed(() => this.hasPermission('core:memberships:remove'));
  readonly canAssignRoles = computed(() => this.hasPermission('core:roles:assign'));
  readonly canManageTeamMembers = computed(() => this.hasPermission('core:teams:manage-members'));
  readonly invitePermissionHint =
    'Você precisa da permissão core:memberships:invite para gerenciar convites e domínios.';
  readonly updateMembershipPermissionHint =
    'Você precisa da permissão core:memberships:update para alterar o status do usuário.';
  readonly removeMembershipPermissionHint =
    'Você precisa da permissão core:memberships:remove para remover usuários.';
  readonly assignRolesPermissionHint =
    'Você precisa da permissão core:roles:assign para alterar funções.';
  readonly teamMembersPermissionHint =
    'Você precisa da permissão core:teams:manage-members para alterar equipes.';
  // W2-5: quem criou o negócio é owner permanente — a API já bloqueia (CreatorProtectionError),
  // inclusive o PRÓPRIO criador se suspendendo/saindo (isso travaria o negócio pra sempre: uma
  // vez inativo, ninguém consegue reverter nem transferir titularidade). Isto só evita o usuário
  // tentar e receber um erro. Pra sair, transfira a titularidade primeiro (Configurações >
  // Usuários), depois saia como owner comum.
  readonly creatorProtectionHint =
    'Quem criou o negócio não pode ser suspenso ou excluído — nem por outra pessoa, nem por você mesmo. Para sair, transfira a titularidade primeiro em "Transferir titularidade".';
  readonly assignableRoles = computed(() =>
    this.roles().filter((role) => role.status === 'active' && role.key !== 'owner'),
  );
  readonly pendingInvitations = computed(() =>
    this.invitations().filter((invitation) => invitation.status === 'pending'),
  );
  readonly visibleMemberships = computed(() =>
    this.memberships().filter(
      (membership) => membership.status === 'active' || membership.status === 'suspended',
    ),
  );
  readonly activeTeams = computed(() => this.teams().filter((team) => team.status !== 'archived'));
  readonly visibleDomains = computed(() =>
    this.domains().filter((domain) => domain.status !== 'archived'),
  );
  readonly membershipStatusOptions = [
    { value: 'active', label: 'Ativo' },
    { value: 'suspended', label: 'Suspenso' },
  ] as const;

  // W2-6: transferência de titularidade — ação exclusiva de quem criou o negócio.
  readonly transferSaving = signal(false);
  readonly isCurrentUserCreator = computed(() =>
    this.memberships().some(
      (m) => m.isCreator === true && m.id === this.authStore.membershipId(),
    ),
  );
  readonly transferCandidates = computed<UiSelectFieldOption[]>(() =>
    this.visibleMemberships()
      .filter((m) => m.status === 'active' && m.id !== this.authStore.membershipId())
      .map((m) => ({ value: m.id, label: this.membershipLabel(m) })),
  );

  readonly inviteForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email, Validators.maxLength(320)]],
    roleId: [''],
  });

  readonly transferForm = this.fb.nonNullable.group({
    targetMembershipId: ['', Validators.required],
  });

  readonly domainForm = this.fb.nonNullable.group({
    domain: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(253)]],
    roleId: [''],
  });

  constructor() {
    this.load();
  }

  load() {
    const businessId = this.businessId();
    if (!businessId) return;

    this.loading.set(true);
    forkJoin({
      roles: this.api.listRoles(businessId),
      memberships: this.api.listMemberships(businessId),
      roleBindings: this.api.listRoleBindings(businessId),
      teams: this.api.listTeams(businessId),
      invitations: this.api.listInvitations(businessId),
      domains: this.api.listEmailDomains(businessId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ roles, memberships, roleBindings, teams, invitations, domains }) => {
          const activeTeams = teams.teams.filter((team) => team.status !== 'archived');
          this.roles.set(roles.roles.filter((role) => role.status === 'active'));
          this.memberships.set(
            memberships.memberships.filter((membership) => membership.status !== 'removed'),
          );
          this.roleBindings.set(roleBindings.roleBindings);
          this.teams.set(activeTeams);
          this.invitations.set(
            invitations.invitations.filter((invitation) => invitation.status === 'pending'),
          );
          this.domains.set(domains.domains.filter((domain) => domain.status !== 'archived'));
          this.ensureDefaultRole();
          this.loadTeamMemberships(activeTeams);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toastr.error('Não foi possível carregar usuários e convites.');
        },
      });
  }

  createInvitation() {
    const businessId = this.businessId();
    if (!businessId || this.inviteForm.invalid) return;
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    const roleId = this.inviteForm.controls.roleId.value;
    if (!roleId) {
      this.toastr.error('Selecione uma função para o convite.');
      return;
    }

    this.saving.set(true);
    this.api
      .createInvitation({
        businessId,
        email: this.inviteForm.controls.email.value.trim(),
        roleIds: [roleId],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (invitation) => {
          this.invitations.update((items) =>
            invitation.status === 'pending' ? [invitation, ...items] : items,
          );
          this.inviteForm.controls.email.setValue('');
          this.saving.set(false);
          this.toastr.success('Convite criado.');
        },
        // O toast de erro é do interceptor HTTP: ele mapeia o code (ex.: `billing_capacity_exceeded`
        // → "Limite do plano atingido" + CTA "Ver planos") e cobre 402/409/422/500 com mensagens
        // específicas. Um toast genérico aqui só duplicaria/mascararia o instrutivo. Só reabilita o form.
        error: () => {
          this.saving.set(false);
        },
      });
  }

  selectInviteRole(value: unknown) {
    if (!this.canInviteMembers()) return;
    this.inviteForm.controls.roleId.setValue(readSelectString(value));
  }

  selectDomainRole(value: unknown) {
    if (!this.canInviteMembers()) return;
    this.domainForm.controls.roleId.setValue(readSelectString(value));
  }

  roleLabel(roleId: string) {
    return this.roles().find((role) => role.id === roleId)?.name ?? 'Selecionar função';
  }

  roleSelectionLabel(roleIds: string[]) {
    if (!roleIds.length) return 'Sem função';
    return roleIds.map((roleId) => this.roleLabel(roleId)).join(', ');
  }

  teamSelectionLabel(teamIds: string[]) {
    if (!teamIds.length) return 'Sem equipe';
    return teamIds
      .map((teamId) => this.activeTeams().find((team) => team.id === teamId)?.name ?? teamId)
      .join(', ');
  }

  membershipStatusLabel(status: CoreMembership['status']) {
    return status === 'suspended' ? 'Suspenso' : 'Ativo';
  }

  isCreatorMembership(membership: CoreMembership) {
    return membership.isCreator === true;
  }

  membershipRoleIds(membershipId: string) {
    const visibleRoleIds = new Set(this.assignableRoles().map((role) => role.id));
    return this.roleBindings()
      .filter(
        (binding) => binding.membershipId === membershipId && visibleRoleIds.has(binding.roleId),
      )
      .map((binding) => binding.roleId);
  }

  membershipTeamIds(membershipId: string) {
    return this.teamMembers()
      .filter((member) => member.membershipId === membershipId)
      .map((member) => member.teamId);
  }

  selectMembershipRoles(membership: CoreMembership, value: unknown) {
    const businessId = this.businessId();
    if (!businessId) return;
    if (!this.requirePermission(this.canAssignRoles(), this.assignRolesPermissionHint)) return;

    const nextRoleIds = readSelectStringArray(value);
    const currentRoleIds = new Set(this.membershipRoleIds(membership.id));
    const nextRoleIdSet = new Set(nextRoleIds);
    const additions = nextRoleIds.filter((roleId) => !currentRoleIds.has(roleId));
    const removals = this.roleBindings().filter(
      (binding) =>
        binding.membershipId === membership.id &&
        currentRoleIds.has(binding.roleId) &&
        !nextRoleIdSet.has(binding.roleId),
    );

    if (!additions.length && !removals.length) return;

    this.markMembershipSaving(membership.id, true);
    void Promise.all([
      ...additions.map((roleId) =>
        firstValueFrom(
          this.api.createRoleBinding({ businessId, membershipId: membership.id, roleId }),
        ),
      ),
      ...removals.map((binding) =>
        firstValueFrom(this.api.deleteRoleBinding(binding.id)).then(() => binding.id),
      ),
    ])
      .then((results) => {
        const removedIds = new Set(
          results.filter((result): result is string => typeof result === 'string'),
        );
        const createdBindings = results.filter(
          (result): result is CoreRoleBinding => typeof result !== 'string',
        );
        this.roleBindings.update((bindings) => [
          ...createdBindings,
          ...bindings.filter((binding) => !removedIds.has(binding.id)),
        ]);
        this.markMembershipSaving(membership.id, false);
        this.toastr.success('Funções atualizadas.');
      })
      .catch(() => {
        this.markMembershipSaving(membership.id, false);
        this.toastr.error('Não foi possível atualizar as funções.');
      });
  }

  selectMembershipTeams(membership: CoreMembership, value: unknown) {
    if (!this.requirePermission(this.canManageTeamMembers(), this.teamMembersPermissionHint))
      return;

    const nextTeamIds = readSelectStringArray(value);
    const currentMembers = this.teamMembers().filter(
      (member) => member.membershipId === membership.id,
    );
    const currentTeamIds = new Set(currentMembers.map((member) => member.teamId));
    const nextTeamIdSet = new Set(nextTeamIds);
    const additions = nextTeamIds.filter((teamId) => !currentTeamIds.has(teamId));
    const removals = currentMembers.filter((member) => !nextTeamIdSet.has(member.teamId));

    if (!additions.length && !removals.length) return;

    this.markMembershipSaving(membership.id, true);
    void Promise.all([
      ...additions.map((teamId) =>
        firstValueFrom(
          this.api.addTeamMember(teamId, { membershipId: membership.id, role: 'member' }),
        ),
      ),
      ...removals.map((member) =>
        firstValueFrom(this.api.removeTeamMember(member.teamId, member.membershipId)).then(
          () => member,
        ),
      ),
    ])
      .then((results) => {
        const removed = new Set(
          results
            .filter(
              (result): result is CoreTeamMember =>
                'teamId' in result && removals.some((member) => member.teamId === result.teamId),
            )
            .map((member) => `${member.teamId}:${member.membershipId}`),
        );
        const createdMembers = results.filter(
          (result): result is CoreTeamMember =>
            'teamId' in result && additions.includes(result.teamId),
        );
        this.teamMembers.update((members) => [
          ...createdMembers,
          ...members.filter((member) => !removed.has(`${member.teamId}:${member.membershipId}`)),
        ]);
        this.markMembershipSaving(membership.id, false);
        this.toastr.success('Equipes atualizadas.');
      })
      .catch(() => {
        this.markMembershipSaving(membership.id, false);
        this.toastr.error('Não foi possível atualizar as equipes.');
      });
  }

  selectMembershipStatus(membership: CoreMembership, value: unknown) {
    const status = readMembershipStatus(value);
    if (!status || status === membership.status) return;
    void this.updateMembershipStatus(membership, status);
  }

  resendInvitation(invitationId: string) {
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    this.markInvitationSaving(invitationId, true);
    this.api
      .resendInvitation(invitationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.replaceInvitation(updated);
          this.markInvitationSaving(invitationId, false);
          this.toastr.success('Convite reenviado.');
        },
        error: () => {
          this.markInvitationSaving(invitationId, false);
          this.toastr.error('Não foi possível reenviar o convite.');
        },
      });
  }

  async revokeInvitation(invitationId: string) {
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    const confirmed = await this.confirm.confirm({
      title: 'Revogar convite?',
      description: 'A pessoa não conseguirá mais entrar por este convite pendente.',
      confirmLabel: 'Revogar',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.markInvitationSaving(invitationId, true);
    this.api
      .revokeInvitation(invitationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.invitations.update((items) => items.filter((item) => item.id !== updated.id));
          this.markInvitationSaving(invitationId, false);
          this.toastr.success('Convite revogado.');
        },
        error: () => {
          this.markInvitationSaving(invitationId, false);
          this.toastr.error('Não foi possível revogar o convite.');
        },
      });
  }

  createDomain() {
    const businessId = this.businessId();
    if (!businessId || this.domainForm.invalid) return;
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    const roleId = this.domainForm.controls.roleId.value;
    this.saving.set(true);
    this.api
      .createEmailDomain({
        businessId,
        domain: this.domainForm.controls.domain.value.trim(),
        autoInviteEnabled: true,
        includeSubdomains: false,
        roleIds: roleId ? [roleId] : [],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (domain) => {
          this.domains.update((items) => [domain, ...items]);
          this.domainForm.controls.domain.setValue('');
          this.saving.set(false);
          this.toastr.success('Domínio criado.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível criar o domínio.');
        },
      });
  }

  verifyDomain(domainId: string) {
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    this.markDomainSaving(domainId, true);
    this.api
      .verifyEmailDomain(domainId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.replaceDomain(updated);
          this.markDomainSaving(domainId, false);
          this.toastr.success('Domínio verificado.');
        },
        error: () => {
          this.markDomainSaving(domainId, false);
          this.toastr.error('Não foi possível verificar o domínio.');
        },
      });
  }

  toggleDomain(domain: CoreEmailDomain) {
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    this.markDomainSaving(domain.id, true);
    this.api
      .updateEmailDomain(domain.id, { autoInviteEnabled: !domain.autoInviteEnabled })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.replaceDomain(updated);
          this.markDomainSaving(domain.id, false);
        },
        error: () => {
          this.markDomainSaving(domain.id, false);
          this.toastr.error('Não foi possível atualizar o domínio.');
        },
      });
  }

  async disableDomain(domainId: string) {
    if (!this.requirePermission(this.canInviteMembers(), this.invitePermissionHint)) return;

    const confirmed = await this.confirm.confirm({
      title: 'Desabilitar domínio?',
      description: 'O auto-convite por este domínio será interrompido para novos usuários.',
      confirmLabel: 'Desabilitar',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.markDomainSaving(domainId, true);
    this.api
      .disableEmailDomain(domainId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.replaceDomain(updated);
          this.markDomainSaving(domainId, false);
          this.toastr.success('Domínio desabilitado.');
        },
        error: () => {
          this.markDomainSaving(domainId, false);
          this.toastr.error('Não foi possível desabilitar o domínio.');
        },
      });
  }

  async updateMembershipStatus(membership: CoreMembership, status: 'active' | 'suspended') {
    if (!this.requirePermission(this.canUpdateMemberships(), this.updateMembershipPermissionHint))
      return;
    if (status === 'suspended' && !this.requirePermission(!this.isCreatorMembership(membership), this.creatorProtectionHint))
      return;

    if (status === 'suspended') {
      const label = this.membershipLabel(membership);
      const confirmed = await this.confirm.confirm({
        title: 'Suspender usuário?',
        description: `${label} perde acesso à organização até ser reativado.`,
        confirmLabel: 'Suspender',
        tone: 'danger',
      });
      if (!confirmed) return;
    }

    this.markMembershipSaving(membership.id, true);
    this.api
      .updateMembershipStatus(membership.businessId, membership.id, status)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.memberships.update((items) =>
            items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
          );
          this.markMembershipSaving(membership.id, false);
          this.toastr.success('Status do usuário atualizado.');
        },
        error: () => {
          this.markMembershipSaving(membership.id, false);
          this.toastr.error('Não foi possível atualizar o usuário.');
        },
      });
  }

  async removeMembership(membership: CoreMembership) {
    if (!this.requirePermission(this.canRemoveMemberships(), this.removeMembershipPermissionHint))
      return;
    if (!this.requirePermission(!this.isCreatorMembership(membership), this.creatorProtectionHint))
      return;

    const label = this.membershipLabel(membership);
    const roleCount = this.membershipRoleIds(membership.id).length;
    const teamCount = this.membershipTeamIds(membership.id).length;
    const confirmed = await this.confirm.confirm({
      title: 'Excluir usuário?',
      description: `${label} será removido da organização, com ${roleCount} funções e ${teamCount} equipes desvinculadas. A conta pessoal não será apagada.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.markMembershipSaving(membership.id, true);
    this.api
      .removeMembership(membership.businessId, membership.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.memberships.update((items) =>
            items.map((item) =>
              item.id === membership.id ? { ...item, status: 'removed' } : item,
            ),
          );
          this.markMembershipSaving(membership.id, false);
          this.toastr.success('Usuário removido.');
        },
        error: () => {
          this.markMembershipSaving(membership.id, false);
          this.toastr.error('Não foi possível remover o usuário.');
        },
      });
  }

  async transferOwnership() {
    const businessId = this.businessId();
    const targetMembershipId = this.transferForm.controls.targetMembershipId.value;
    if (!businessId || !targetMembershipId) {
      this.toastr.error('Selecione um membro para transferir a titularidade.');
      return;
    }

    const target = this.memberships().find((m) => m.id === targetMembershipId);
    const label = target ? this.membershipLabel(target) : 'este membro';
    const confirmed = await this.confirm.confirm({
      title: 'Transferir titularidade?',
      description: `${label} passa a ser o titular do negócio (owner permanente). Esta ação é IRREVERSÍVEL: você deixa de ser o criador protegido e vira um owner comum, podendo ser removido por outros owners a partir de agora.`,
      confirmLabel: 'Transferir titularidade',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.transferSaving.set(true);
    this.api
      .transferOwnership(businessId, targetMembershipId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.memberships.update((items) =>
            items.map((item) => ({ ...item, isCreator: item.id === targetMembershipId })),
          );
          this.transferForm.reset();
          this.transferSaving.set(false);
          this.toastr.success('Titularidade transferida.');
        },
        error: () => {
          this.transferSaving.set(false);
          this.toastr.error('Não foi possível transferir a titularidade.');
        },
      });
  }

  isMembershipSaving(membershipId: string) {
    return this.membershipSavingIds().includes(membershipId);
  }

  isInvitationSaving(invitationId: string) {
    return this.invitationSavingIds().includes(invitationId);
  }

  isDomainSaving(domainId: string) {
    return this.domainSavingIds().includes(domainId);
  }

  private loadTeamMemberships(teams: CoreTeam[]) {
    if (!teams.length) {
      this.teamMembers.set([]);
      return;
    }

    forkJoin(teams.map((team) => this.api.listTeamMembers(team.id)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => this.teamMembers.set(results.flatMap((result) => result.members)),
        error: () => this.toastr.error('Não foi possível carregar vínculos de equipes.'),
      });
  }

  private ensureDefaultRole() {
    const current = this.inviteForm.controls.roleId.value;
    if (current) return;
    const role = this.assignableRoles()[0];
    if (!role) return;
    this.inviteForm.controls.roleId.setValue(role.id);
    this.domainForm.controls.roleId.setValue(role.id);
  }

  private replaceInvitation(invitation: CoreInvitation) {
    this.invitations.update((items) =>
      items.map((item) => (item.id === invitation.id ? invitation : item)),
    );
  }

  private replaceDomain(domain: CoreEmailDomain) {
    this.domains.update((items) => items.map((item) => (item.id === domain.id ? domain : item)));
  }

  private membershipLabel(membership: CoreMembership) {
    return membership.user?.displayName || membership.user?.primaryEmail || membership.id;
  }

  private hasPermission(permission: AuthPermission) {
    return this.authStore.hasEveryPermission([permission]);
  }

  private requirePermission(allowed: boolean, message: string) {
    if (allowed) {
      return true;
    }

    this.toastr.error(message);
    return false;
  }

  private markMembershipSaving(membershipId: string, saving: boolean) {
    this.membershipSavingIds.update((ids) =>
      saving ? [...new Set([...ids, membershipId])] : ids.filter((id) => id !== membershipId),
    );
  }

  private markInvitationSaving(invitationId: string, saving: boolean) {
    this.invitationSavingIds.update((ids) =>
      saving ? [...new Set([...ids, invitationId])] : ids.filter((id) => id !== invitationId),
    );
  }

  private markDomainSaving(domainId: string, saving: boolean) {
    this.domainSavingIds.update((ids) =>
      saving ? [...new Set([...ids, domainId])] : ids.filter((id) => id !== domainId),
    );
  }
}

function readSelectString(value: unknown) {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }

  return typeof value === 'string' ? value : '';
}

function readSelectStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return typeof value === 'string' && value ? [value] : [];
}

function readMembershipStatus(value: unknown): 'active' | 'suspended' | null {
  const status = readSelectString(value);
  return status === 'active' || status === 'suspended' ? status : null;
}
